/**
 * Phase 87av — PDF Courrier corporate Maison Thaï.
 *
 * Reprend la même charte visuelle que CateringQuotePdf (norme suisse, adresse
 * destinataire à droite, logo + société à gauche, footer compagnie).
 *
 * Le corps du courrier est un document Tiptap JSON rendu via tiptapPdfRenderer.
 */
import React from "react";
import { Document, Page, View, Text, Image, StyleSheet } from "@react-pdf/renderer";
import renderTiptap from "./tiptapPdfRenderer";
import { COMPANY_HEADER } from "../lib/company";

const TYPE_META = {
  INFORMATION:           { label: "INFORMATION",         color: "#1F4E3D", subtitle: "Courrier d'information" },
  WARNING:               { label: "MISE EN GARDE",       color: "#8B1A1A", subtitle: "Notification importante" },
  VENDING_COMMUNICATION: { label: "COMMUNICATION VENDING", color: "#8C6B2F", subtitle: "Distributeur — résultats & statistiques" },
  COMMERCIAL:            { label: "COMMUNICATION",       color: "#C1A063", subtitle: "Communication commerciale" },
  REMINDER:              { label: "RAPPEL",              color: "#735524", subtitle: "Relance / rappel" },
  ANNOUNCEMENT:          { label: "ANNONCE",             color: "#1F4E3D", subtitle: "Annonce officielle" },
};

// L'utilisateur peut saisir un type libre (Phase 87av — choix 2b). Si le type
// ne matche aucune clé de TYPE_META, on prend le label tel-quel et une couleur
// dorée par défaut, conservant la mise en page uniforme.
function resolveTypeMeta(rawType) {
  if (!rawType) return { label: "COURRIER", color: "#1F4E3D", subtitle: "Communication officielle" };
  const upper = String(rawType).toUpperCase();
  if (TYPE_META[upper]) return TYPE_META[upper];
  return { label: String(rawType).toUpperCase(), color: "#8C6B2F", subtitle: "Communication officielle" };
}

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-CH", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
};

const styles = StyleSheet.create({
  // Phase 101dd — paddingTop 80pt sur toutes les pages (au lieu de 36pt).
  // La page 1 compense via senderRow.marginTop=-44pt (garde la position originale
  // de l'émetteur en haut). Les pages 2+ héritent naturellement des 80pt supplémentaires
  // = marge haute plus généreuse pour le texte de continuation.
  page: { paddingTop: 80, paddingRight: 36, paddingBottom: 36, paddingLeft: 36, fontFamily: "Helvetica", fontSize: 10, color: "#1A1C1A" },

  // ╭─────────────────────────────────────────────────────────────╮
  // │ Norme suisse C5 avec guichet à droite (Poste suisse / DIN  │
  // │ 5008) :                                                     │
  // │ • L'adresse destinataire doit apparaître dans une fenêtre   │
  // │   d'env. 90×45 mm située à ~50 mm du haut de la feuille    │
  // │   et à ~110 mm du bord gauche.                              │
  // │ • Notre logo + bloc société restent en haut, pleine         │
  // │   largeur, AVANT l'adresse (cf. brief 87av-bis).            │
  // ╰─────────────────────────────────────────────────────────────╯

  // En-tête émetteur : pleine largeur en haut de page (page 1 uniquement).
  // marginTop négatif remonte de -44pt pour compenser le paddingTop 80pt
  // (36pt effectif conservé sur page 1, comme historiquement).
  senderRow: { flexDirection: "row", alignItems: "flex-start", gap: 10, marginTop: -44, marginBottom: 4 },
  senderLogo: { width: 50, height: 50 },
  senderInfo: { flexDirection: "column", flex: 1 },
  senderName: { fontSize: 16, fontFamily: "Helvetica-Bold", marginBottom: 2, letterSpacing: 0.6 },
  senderMeta: { fontSize: 7.5, color: "#575A57", lineHeight: 1.4 },

  // Adresse destinataire — positionnement absolu calibré pour
  // tomber dans le guichet d'une enveloppe C5 à fenêtre droite.
  //   • top   : 105 pt + padding-page 36 pt ≈ 141 pt ≈ 49.8 mm du haut feuille
  //   • right : 0  → calé contre la marge intérieure droite (36 pt = 12.7 mm)
  //   • width : 230 pt ≈ 81 mm (fenêtre standard 90 mm)
  recipientBlock: {
    position: "absolute",
    top: 141,           // 50 mm du haut de la feuille A4
    right: 36,          // marge droite cohérente avec padding
    width: 230,         // ~81 mm — tient dans une fenêtre 90 mm
    flexDirection: "column",
    paddingLeft: 10,
    borderLeft: "2pt solid #C1A063",
  },
  recipientLabel: { fontSize: 6.5, color: "#A3A6A3", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 },
  recipientName: { fontSize: 11, fontFamily: "Helvetica-Bold", marginBottom: 1 },
  recipientLine: { fontSize: 10, marginBottom: 0.5 },

  // Titre + référence — décalé pour passer SOUS le bloc destinataire
  // absolu (qui se termine vers ~225 pt du haut content = ~80 mm du haut feuille).
  // Sender row ~75 pt + marginTop 150 pt = title @ ~225 pt content → ~80 mm feuille
  // → 4-5 lignes vides séparent visuellement l'adresse et le titre.
  titleRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-end", marginTop: 150, marginBottom: 16, paddingBottom: 8, borderBottom: "1.5pt solid #1A1C1A" },
  titleLeft: { flexDirection: "column" },
  titleMain: { fontSize: 18, fontFamily: "Helvetica-Bold", letterSpacing: 1 },
  titleSub: { fontSize: 8.5, color: "#575A57", marginTop: 2 },
  titleRight: { flexDirection: "column", alignItems: "flex-end" },
  titleRef: { fontSize: 10, fontFamily: "Helvetica-Bold" },
  titleDate: { fontSize: 8.5, color: "#575A57", marginTop: 1 },

  // Sujet
  subjectBox: { marginBottom: 14, paddingBottom: 8, borderBottom: "0.5pt solid #E5E5E0" },
  subjectLabel: { fontSize: 8, color: "#A3A6A3", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 2 },
  subjectText: { fontSize: 12, fontFamily: "Helvetica-Bold", color: "#1A1C1A" },

  // Corps
  body: { marginBottom: 20 },

  // Signature
  signatureBlock: { marginTop: 30 },
  signatureLine: { fontSize: 10, marginBottom: 2 },
  signatureRole: { fontSize: 8.5, color: "#575A57", marginTop: 6 },

  // Footer
  footer: { position: "absolute", bottom: 22, left: 36, right: 36, fontSize: 7, color: "#A3A6A3", textAlign: "center", borderTop: "0.5pt solid #E5E5E0", paddingTop: 6 },
});

export function CompanyLetterPdf({ letter, settings }) {
  const t = resolveTypeMeta(letter.letter_type);
  const isCH = !letter.recipient_country || letter.recipient_country === "CH" || letter.recipient_country === "Suisse";
  return (
    <Document>
      <Page size="A4" style={styles.page}>

        {/* Émetteur — pleine largeur en haut de page (norme C5 : LOGO + société
            avant l'adresse destinataire pour éviter le chevauchement avec le
            guichet de l'enveloppe). */}
        <View style={styles.senderRow}>
          {settings?.company_logo_url
            ? <Image src={settings.company_logo_url} style={styles.senderLogo} />
            : <Image src={`${typeof window !== "undefined" ? window.location.origin : ""}/logo-maisonthai-pdf.png`} style={styles.senderLogo} />}
          <View style={styles.senderInfo}>
            <Image
              src={`${typeof window !== "undefined" ? window.location.origin : ""}/company-name-sayonara.png`}
              style={{ width: 159, height: 16, marginBottom: 2 }}
            />
            {(settings?.company_address || "").split("\n").filter(Boolean).map((line, i) => (
              <Text key={i} style={styles.senderMeta}>{line}</Text>
            ))}
            {settings?.company_postal_code && settings?.company_city && (
              <Text style={styles.senderMeta}>CH-{settings.company_postal_code} {settings.company_city}</Text>
            )}
            {settings?.company_phone && <Text style={styles.senderMeta}>Tél : {settings.company_phone}</Text>}
            {settings?.company_email && <Text style={styles.senderMeta}>{settings.company_email}</Text>}
            {settings?.company_vat_number && <Text style={styles.senderMeta}>TVA : {settings.company_vat_number}</Text>}
          </View>
        </View>

        {/* Destinataire — POSITION ABSOLUE calibrée pour le guichet C5 droit
            (≈ 50 mm du haut feuille / 110 mm du bord gauche). Indépendant du
            contenu de l'émetteur ci-dessus. */}
        <View style={styles.recipientBlock}>
          <Text style={styles.recipientLabel}>Destinataire</Text>
          {letter.recipient_company ? (
            <>
              <Text style={styles.recipientName}>{letter.recipient_company}</Text>
              {letter.recipient_name ? <Text style={styles.recipientLine}>À l&apos;attention de {letter.recipient_name}</Text> : null}
            </>
          ) : (
            <Text style={styles.recipientName}>{letter.recipient_name || "Destinataire"}</Text>
          )}
          {letter.recipient_address ? <Text style={styles.recipientLine}>{letter.recipient_address}</Text> : null}
          {(letter.recipient_postal_code || letter.recipient_city) && (
            <Text style={styles.recipientLine}>
              {isCH ? "CH-" : ""}{letter.recipient_postal_code} {letter.recipient_city}
            </Text>
          )}
          {!isCH && letter.recipient_country ? <Text style={styles.recipientLine}>{letter.recipient_country}</Text> : null}
        </View>

        {/* Titre + référence */}
        <View style={styles.titleRow}>
          <View style={styles.titleLeft}>
            <Text style={[styles.titleMain, { color: t.color }]}>{t.label}</Text>
            <Text style={styles.titleSub}>{t.subtitle}</Text>
          </View>
          <View style={styles.titleRight}>
            <Text style={styles.titleRef}>N° {letter.reference}</Text>
            <Text style={styles.titleDate}>{fmtDate(letter.letter_date)}</Text>
          </View>
        </View>

        {/* Sujet */}
        {letter.subject ? (
          <View style={styles.subjectBox}>
            <Text style={styles.subjectLabel}>Objet</Text>
            <Text style={styles.subjectText}>{letter.subject}</Text>
          </View>
        ) : null}

        {/* Corps Tiptap → react-pdf */}
        <View style={styles.body}>
          {renderTiptap(letter.body_json)}
        </View>

        {/* Signature — Phase 101dd v2 (Feb 2026)
            Ordre demandé par l'utilisatrice :
              1. Cordialement,
              2. Nom de la société (Maison Thaï Conthey)
              3. Nom(s) du/des signataire(s)
              4. (rôle si présent — petit et discret)
              5. Signature scannée (opt-in)
        */}
        <View style={styles.signatureBlock}>
          <Text style={styles.signatureLine}>
            {settings?.company_signature_intro || "Cordialement,"}
          </Text>
          <Text style={[styles.signatureLine, { fontFamily: "Helvetica-Bold", marginTop: 10 }]}>
            {COMPANY_HEADER}
          </Text>
          {(settings?.company_signature_name || "").split("\n").filter((s) => s.trim()).map((n, i) => (
            <Text key={i} style={styles.signatureLine}>
              {n.trim()}
            </Text>
          ))}
          {settings?.company_signature_role ? (
            <Text style={styles.signatureRole}>{settings.company_signature_role}</Text>
          ) : null}
          {/* Signature scannée en dernier — sous les noms, opt-in par courrier */}
          {letter.include_signature_image && settings?.company_signature_image ? (
            <Image
              src={settings.company_signature_image}
              style={{ width: 140, height: 56, marginTop: 6, objectFit: "contain" }}
            />
          ) : null}
        </View>

        <Text style={styles.footer}>
          {COMPANY_HEADER}
          {settings?.company_address ? ` — ${settings.company_address.replace(/\n/g, ", ")}` : ""}
          {settings?.company_postal_code && settings?.company_city ? ` — CH-${settings.company_postal_code} ${settings.company_city}` : ""}
          {` — ${letter.reference}`}
        </Text>
      </Page>
    </Document>
  );
}

export default CompanyLetterPdf;
